import { useState, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, Button, InputAdornment, Autocomplete } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { listAreas } from '../../services/areaService';

const CommercialFilter = ({
    onSearch,
    filters: externalFilters,
    onFilterChange,
    showTypeField = false
}) => {
    const [internalFilters, setInternalFilters] = useState({
        type: 'commercial',
        keyword: '',
        area: '',
        sqYards: ''
    });
    const [openSelects, setOpenSelects] = useState({});
    const [areaOptions, setAreaOptions] = useState([]);

    useEffect(() => {
        const fetchAreas = async () => {
            try {
                const data = await listAreas();
                if (data && Array.isArray(data)) {
                    setAreaOptions(data);
                }
            } catch (error) {
                console.error("Failed to fetch areas", error);
            }
        };
        fetchAreas();
    }, []);

    useEffect(() => {
        const closeAll = () => setOpenSelects({});
        window.addEventListener('scroll', closeAll, { passive: true });
        window.addEventListener('wheel', closeAll, { passive: true });
        return () => {
            window.removeEventListener('scroll', closeAll);
            window.removeEventListener('wheel', closeAll);
        };
    }, []);

    const filters = externalFilters || internalFilters;

    const filterConfig = [
        {
            type: 'search',
            field: 'keyword',
            placeholder: 'Enter Keyword...',
            flex: 2,
            icon: SearchIcon
        },
        {
            type: 'select',
            field: 'type',
            placeholder: 'Type',
            flex: 1,
            disabled: true,
            options: [
                { value: 'commercial', label: 'Commercial' }
            ]
        },
        {
            type: 'select',
            field: 'area',
            placeholder: 'All Areas',
            flex: 1,
            options: areaOptions
        }
    ];
    filterConfig.push(
        {
            type: 'number',
            field: 'sqYards',
            placeholder: 'Sq Yards',
            flex: 1
        }
    );

    const handleInputChange = (field, value) => {
        const newFilters = { ...filters, [field]: value };

        if (onFilterChange) {
            onFilterChange(newFilters);
        } else {
            setInternalFilters(newFilters);
        }
    };

    const handleSearch = () => {
        if (onSearch) {
            onSearch(filters, true); // Pass true to indicate this is a search action
        }
    };

    const handleClear = () => {
        const clearedFilters = {
            type: 'commercial',
            keyword: '',
            area: '',
            sqYards: ''
        };

        if (onFilterChange) {
            onFilterChange(clearedFilters);
        } else {
            setInternalFilters(clearedFilters);
        }

        // Trigger search immediately with cleared filters
        if (onSearch) {
            onSearch(clearedFilters, true);
        }
    };
    const renderField = (config) => {
        const isDisabled = config.disabled;

        const commonStyles = {
            flex: { xs: '1 1 100%', md: config.flex },
            '& .MuiOutlinedInput-root': {
                backgroundColor: isDisabled ? '#f0f0f0' : '#fafafa',
                fontSize: '14px',
                borderRadius: '4px',
                '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderRadius: '4px'
                },
                '&:hover fieldset': {
                    borderColor: isDisabled ? '#e0e0e0' : '#bdbdbd'
                }
            }
        };

        if (config.type === 'search') {
            const Icon = config.icon;
            return (
                <TextField
                    key={config.field}
                    placeholder={config.placeholder}
                    value={filters[config.field] || ''}
                    onChange={(e) => handleInputChange(config.field, e.target.value)}
                    size="small"
                    disabled={isDisabled}
                    sx={{
                        ...commonStyles,
                        '& input::placeholder': {
                            color: '#999',
                            opacity: 1
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon sx={{ color: '#bdbdbd', fontSize: '20px' }} />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            );
        }

        if (config.type === 'number') {
            return (
                <TextField
                    key={config.field}
                    placeholder={config.placeholder}
                    value={filters[config.field] || ''}
                    onChange={(e) => handleInputChange(config.field, e.target.value)}
                    size="small"
                    type="number"
                    disabled={isDisabled}
                    sx={{
                        ...commonStyles,
                        '& input::placeholder': {
                            color: '#999',
                            opacity: 1
                        }
                    }}
                />
            );
        }
        if (config.type === 'select') {
            // Special handling for area field - make it searchable
            if (config.field === 'area') {
                return (
                    <Autocomplete
                        key={config.field}
                        fullWidth
                        size="small"
                        options={config.options}
                        getOptionLabel={(option) => option.label || ''}
                        value={config.options.find(option => option.value === filters[config.field]) || null}
                        onChange={(event, value) => handleInputChange(config.field, value ? value.value : '')}
                        disabled={isDisabled}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={config.placeholder}
                                sx={{
                                    ...commonStyles,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: isDisabled ? '#f0f0f0' : '#fafafa',
                                        fontSize: '14px',
                                        borderRadius: '4px',
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                            borderRadius: '4px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: isDisabled ? '#e0e0e0' : '#bdbdbd'
                                        }
                                    },
                                    '& input::placeholder': {
                                        color: '#999',
                                        opacity: 1
                                    }
                                }}
                            />
                        )}
                        sx={{
                            flex: { xs: '1 1 100%', md: config.flex }
                        }}
                    />
                );
            }

            // Regular select for other fields
            return (
                <Select
                    key={config.field}
                    value={filters[config.field] || ''}
                    onChange={(e) => handleInputChange(config.field, e.target.value)}
                    displayEmpty
                    size="small"
                    disabled={isDisabled}
                    sx={{
                        ...commonStyles,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e0e0e0'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: isDisabled ? '#e0e0e0' : '#bdbdbd'
                        },
                        '& .MuiSelect-select': {
                            color: filters[config.field] ? '#333' : '#999'
                        }
                    }}
                >
                    <MenuItem value="" sx={{ fontSize: '14px', color: '#999' }}>
                        {config.placeholder}
                    </MenuItem>
                    {config.options.map(option => (
                        <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            );
        }

        return null;
    };
    return (
        <div
            style={{
                paddingTop: '20px',
                paddingBottom: '20px',
                backgroundColor: '#f5f5f5'
            }}
        >
            <div className="container">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1,
                        alignItems: 'stretch',
                        backgroundColor: '#fff',
                        p: 1.5,
                        borderRadius: '4px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    {filterConfig.map(config => renderField(config))}

                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        size="small"
                        sx={{
                            minWidth: '70px',
                            color: '#666',
                            borderColor: '#e0e0e0',
                            fontSize: '13px',
                            fontWeight: '500',
                            textTransform: 'none',
                            borderRadius: '4px',
                            '&:hover': {
                                borderColor: '#bdbdbd',
                                backgroundColor: '#fafafa'
                            }
                        }}
                    >
                        Clear
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        size="small"
                        sx={{
                            minWidth: '70px',
                            backgroundColor: '#D8B025',
                            color: '#FFFFFF',
                            fontSize: '13px',
                            fontWeight: '600',
                            textTransform: 'none',
                            borderRadius: '4px',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#c5a02f',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Search
                    </Button>
                </Box>
            </div>
        </div>
    );
};

export default CommercialFilter;